/** @jsxRuntime classic */
/** @jsx jsx */

import {jsx, css} from '@emotion/react'
import React from 'react'

import type {
  Message as MessageInterface,
  ProjectInterface,
} from '../../types/interfaces'
import {colors} from '../Styles'
import replaceWhiteSpaceWith from '../Utils/helpers'
import Title from './Title'

interface OnToggleProps {
  items: Array<ProjectInterface | MessageInterface>
  children: React.ReactNode
  displayedData: ProjectInterface | MessageInterface | undefined
  setDisplayData: React.Dispatch<
    React.SetStateAction<ProjectInterface | MessageInterface | undefined>
  >
}

const closeBtnWrapper = css`
  display: flex;
  place-content: flex-end;
  margin-right: 5px;
`
const closeBtn = css`
  background: ${colors.darkBlue};
  color: ${colors.aliceLightBlue};
  border: 5px solid ${colors.darkBlue};
  font-weight: bolder;
  border-radius: 32%;
  padding: 0px 10px;
  :hover {
    cursor: pointer;
    opacity: 0.8;
  }
`
const disclosureNavWrapper = css`
  display: flex;
  margin: 0 5px;
  border-top: 22px solid ${colors.darkBlue};
  padding: 21px 0;
  border-radius: 13%;
  border-bottom: 22px solid ${colors.darkBlue};
  place-items: center;
  place-content: space-between;
  gap: 10px;
  margin-bottom: 16px;
`
const btnStyle = css`
  border: 5px solid ${colors.darkBlue};
  height: 50px;
  width: 25px;
  :hover {
    cursor: pointer;
    background: ${colors.kindaDarkBlue};
  }
`
const prevBtn = css`
  border-radius: 23px 0 0 24px;
  ${btnStyle}
`
const btnNext = css`
  border-radius: 0 23px 24px 0;
  ${btnStyle}
`
const navTitleWrapper = css`
  width: 100%;
  display: flex;
  place-content: space-evenly;
`
const titleCustomCss = css`
  @keyframes example {
    from {
      opacity: 0.4;
    }
    to {
      opacity: 1;
    }
  }
  opacity: 1;
  animation-name: example;
  animation-duration: 3s;
`

const OnToggle = React.forwardRef(
  ({items, setDisplayData, children, displayedData}: OnToggleProps, ref) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const containerRef = React.useRef<any>()
    const [touchStart, setTouchStart] = React.useState<
      React.TouchEvent | React.MouseEvent | undefined | number | React.TouchList
    >()
    const [touchEnd, setTouchEnd] = React.useState<
      React.TouchEvent | React.MouseEvent | undefined | number | React.TouchList
    >()

    function moveFocus() {
      containerRef.current.focus()
    }

    React.useImperativeHandle(ref, () => ({
      moveFocus,
    }))

    const [show, setShow] = React.useState(() => {
      const i = items.findIndex(item => item.name === displayedData?.name)
      if (window.innerWidth >= 1220) {
        if (i === items.length - 1) return {min: i - 3, max: i, range: 4}
        return {min: i, max: i + 3, range: 4}
      }
      if (window.innerWidth >= 900) {
        if (i === items.length - 1) return {min: i - 2, max: i, range: 3}
        return {min: i, max: i + 2, range: 3}
      }
      if (window.innerWidth >= 480) {
        if (i === items.length - 1) return {min: i - 1, max: i, range: 2}
        return {min: i, max: i + 1, range: 2}
      }
      return {min: i, max: i, range: 1}
    })

    const handlePrevious = React.useCallback(() => {
      if (show.min > 0) {
        setShow({...show, min: show.min - 1, max: show.max - 1})
      }
    }, [show])

    const handleNext = React.useCallback(() => {
      if (show.max <= items.length - 1) {
        setShow({...show, max: show.max + 1, min: show.min + 1})
      }
    }, [items.length, show])

    const handleTouch = React.useCallback(
      (end, start) => {
        if (end > start) {
          handlePrevious()
        }
        if (end < start) {
          handleNext()
        }
      },
      [handleNext, handlePrevious],
    )

    return (
      <section id="disclosure">
        <div css={closeBtnWrapper}>
          <button
            type="button"
            css={closeBtn}
            ref={containerRef}
            data-testid="close-toggler"
            onClick={() => setDisplayData(undefined)}
            aria-current="false"
            aria-label="click to close project disclosure"
            aria-controls="disclosure"
          >
            X
          </button>
        </div>
        <div css={disclosureNavWrapper} role="navigation" id="disclosure-nav">
          <button
            onClick={handlePrevious}
            type="button"
            aria-controls="disclosure-nav"
            data-testid="before-toggle"
            css={[
              prevBtn,
              {
                background:
                  show.min === 0 ? colors.burgundyRed : colors.darkBlue,
              },
            ]}
            disabled={show.min === 0}
            aria-label="click to view previous project"
          />
          <div
            aria-controls="disclosure-view"
            css={navTitleWrapper}
            onMouseDown={e => {
              e.preventDefault()
              setTouchStart(e.screenX)
            }}
            onMouseUp={e => {
              setTouchEnd(e.screenX)
              handleTouch(e.screenX, touchStart)
            }}
            onTouchStart={(e: React.TouchEvent) => {
              setTouchStart(e.changedTouches)
            }}
            onTouchEnd={e => {
              setTouchEnd(e.changedTouches)
              if (Array.isArray(touchStart) && Array.isArray(touchEnd)) {
                if (touchEnd.length > 0 && touchStart.length > 0) {
                  if (e.changedTouches[0].screenY - touchStart[0].screenY < 3) {
                    handleTouch(
                      e.changedTouches[0].screenX,
                      touchStart[0].screenX,
                    )
                  }
                }
              }
            }}
          >
            {items.map((item, i) => {
              if (i >= show.min && i <= show.max) {
                const dashifiedName = replaceWhiteSpaceWith(item.name ?? '')
                return (
                  <Title
                    key={`${dashifiedName}-${i * 2}`}
                    name={item.name}
                    csx={titleCustomCss}
                    onClick={() => {
                      setDisplayData(item)
                    }}
                    testId={`${dashifiedName}-title`}
                    highlight={displayedData?.id === item.id}
                  />
                )
              }
              return null
            })}
          </div>
          <button
            onClick={handleNext}
            data-testid="next-toggle"
            type="button"
            aria-controls="disclosure-nav"
            css={[
              btnNext,
              {
                background:
                  show.max >= items.length - 1
                    ? colors.burgundyRed
                    : colors.darkBlue,
              },
            ]}
            disabled={show.max >= items.length - 1}
            aria-label="click to view next project"
          />
        </div>
        <article id="disclosure-view">{children}</article>
      </section>
    )
  },
)

export default OnToggle
